// ─────────────────────────────────────────────
//  MdCallPanel — MD call list sorted by call order (1-30).
//  Props:
//    doctors — the day's ASSIGNED doctors (derived from assignment rows),
//              enriched with callOrder + name. No manual add/remove: the
//              list is a read-only reflection of the assignment grid.
//
//  Renders a Card titled "MD Call" with doctors sorted by callOrder
//  ascending. Each row: callOrder pill | doctor name.
//  Closing UAT Gap 4 — whole-operation MD-call view.
// ─────────────────────────────────────────────
import React from "react";
import tokens from "../theme/tokens";
import Card from "./Card";
import { sortDoctorsByCallOrder, callOrderLabel } from "../domain/schedule";
import { sortMdCall, mdCallBadge, mdCallLocationRole, mdCallBadgeColor } from "../features/sheet-import/attendingCallList";

const { color, font, space, radius } = tokens;

// callList (preferred, from the sheet): facility-wide attendings with
// { code, name, callOrder, postCall, wc }. Sorted PC1-6 → 1..N → L; WC = red
// circle, others = blue. Falls back to `doctors` (rostered) when no sheet import.
// `facilities` = the tenant's live facility docs, used to name and colour each
// campus's leader tag. Optional: with none, the built-in seed's colours and
// labels apply, which is exactly this deployment's existing behaviour.
export default function MdCallPanel({ doctors = [], callList = null, facilityBoards = {}, facilities = [] }) {
  const useSheet = Array.isArray(callList) && callList.length > 0;
  const listed = useSheet ? sortMdCall(callList) : sortDoctorsByCallOrder(doctors);
  // Location colour-coding: the NUMBER BADGE is tinted by the campus the
  // doctor works at today (red WC / blue Tampa / green CAR); NAMES STAY
  // BLACK. Campus leaders additionally get their labelled tag.
  const roleOf = (e) => mdCallLocationRole(e, facilityBoards.aicByFacility || {}, facilities);
  const badgeColorOf = (e) => mdCallBadgeColor(e, { ...facilityBoards, facilities });

  return (
    <Card style={st.card}>
      <h2 style={st.heading}>MD Call</h2>

      {listed.length === 0 ? (
        <p style={st.empty}>No doctors assigned yet.</p>
      ) : (
        <ul style={st.list} aria-label="MD call list">
          {useSheet
            ? listed.map((e) => {
                const role = roleOf(e);
                return (
                <li key={e.code} style={st.row}>
                  <span
                    style={{ ...st.numPill, background: badgeColorOf(e) || (e.wc ? color.accent.error : color.accent.primary) }}
                    title={role?.label}
                  >
                    {mdCallBadge(e)}
                  </span>
                  <span style={st.name}>{e.name || e.code}</span>
                  {role && <span style={{ ...st.aicTag, color: role.color, borderColor: role.color, background: "transparent" }}>{role.label}</span>}
                  {!role && e.wc && e.isAIC && <span style={st.aicTag}>AIC</span>}
                </li>
                );
              })
            : listed.map((doctor) => {
                const role = roleOf(doctor);
                return (
                <li key={doctor.id} style={st.row}>
                  <span style={{ ...st.numPill, ...(badgeColorOf(doctor) ? { background: badgeColorOf(doctor) } : {}) }} aria-hidden="true">
                    {callOrderLabel(doctor.callOrder)}
                  </span>
                  <span style={st.name}>{doctor.name}</span>
                  {role && <span style={{ ...st.aicTag, color: role.color, borderColor: role.color, background: "transparent" }}>{role.label}</span>}
                </li>
                );
              })}
        </ul>
      )}
    </Card>
  );
}

const st = {
  card: {
    fontFamily: font.family,
  },
  heading: {
    margin: `0 0 ${space.md}px`,
    fontSize: font.size.heading,
    fontWeight: font.weight.bold,
    color: color.text.primary,
    lineHeight: 1.2,
  },
  list: {
    listStyle: "none",
    margin: 0,
    padding: 0,
  },
  row: {
    display: "flex",
    alignItems: "center",
    gap: space.sm,
    padding: `${space.xs}px 0`,
    borderBottom: `1px solid ${color.border.default}`,
    minHeight: 44, // 44px min touch target per accessibility rules
  },
  // Navy call-order badge matching AvailabilitySection.callOrderPill.
  numPill: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    minWidth: 26,
    height: 26,
    padding: "0 6px",
    borderRadius: radius.full,
    background: color.accent.primary,
    color: "#ffffff",
    fontSize: font.size.label,
    fontWeight: font.weight.bold,
    flexShrink: 0,
  },
  aicTag: {
    fontSize: 10,
    fontWeight: font.weight.bold,
    color: color.accent.primary,
    background: "#1e40af14",
    border: `1px solid ${color.accent.primary}`,
    borderRadius: radius.sm,
    padding: "1px 5px",
    flexShrink: 0,
  },
  name: {
    flex: 1,
    fontSize: font.size.body,
    color: color.text.primary,
  },
  empty: {
    textAlign: "center",
    fontSize: font.size.body,
    color: color.text.muted,
    margin: `${space.md}px 0`,
  },
};
